export const AUTH_RETURN_PARAM = 'returnTo';
export const LEGACY_AUTH_RETURN_PARAM = 'redirect';
export const DEFAULT_AUTH_RETURN_TO = '/';

const INTERNAL_ORIGIN = 'https://light-display-market.internal';

function parseInternalPath(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;

  const candidate = value.trim();
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) return null;
  if (candidate.includes('\\')) return null;

  try {
    const parsed = new URL(candidate, INTERNAL_ORIGIN);
    if (parsed.origin !== INTERNAL_ORIGIN) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/**
 * Restrict an auth return target to a same-origin application path.
 * Query strings and fragments are retained so deep links survive auth.
 */
export function normalizeAuthReturnTo(
  value: string | null | undefined,
  fallback: string = DEFAULT_AUTH_RETURN_TO,
): string {
  const safeFallback = parseInternalPath(fallback) ?? DEFAULT_AUTH_RETURN_TO;
  return parseInternalPath(value) ?? safeFallback;
}

/** Read the new returnTo parameter, accepting redirect for older links/callbacks. */
export function getAuthReturnTo(
  input: URLSearchParams | string | null | undefined,
  fallback: string = DEFAULT_AUTH_RETURN_TO,
): string {
  const params = input instanceof URLSearchParams
    ? input
    : new URLSearchParams(typeof input === 'string' ? input.replace(/^\?/, '') : '');

  const requested = params.get(AUTH_RETURN_PARAM) ?? params.get(LEGACY_AUTH_RETURN_PARAM);
  return normalizeAuthReturnTo(requested, fallback);
}

/** Build an auth entry-point URL with the normalized target encoded as returnTo. */
export function buildAuthHref(
  authPath: string = '/auth',
  returnTo: string = DEFAULT_AUTH_RETURN_TO,
): string {
  const separator = authPath.includes('?') ? '&' : '?';
  return `${authPath}${separator}${AUTH_RETURN_PARAM}=${encodeURIComponent(normalizeAuthReturnTo(returnTo))}`;
}

/** The returnTo a visitor arrived with, or null when the URL carries none. */
export function readExplicitReturnTo(input: URLSearchParams): string | null {
  return input.has(AUTH_RETURN_PARAM) || input.has(LEGACY_AUTH_RETURN_PARAM)
    ? getAuthReturnTo(input)
    : null;
}

/** Carry a returnTo along to the next auth page; leave the path bare when there is none. */
export function withReturnTo(path: string, returnTo: string | null): string {
  return returnTo ? buildAuthHref(path, returnTo) : path;
}

/**
 * Where the confirmation email link lands: the callback on this origin, with
 * the returnTo in the query so it survives a new tab or another device.
 */
export function buildEmailCallbackUrl(origin: string, returnTo: string | null = null): string {
  return withReturnTo(`${origin}/auth/callback`, returnTo);
}
